/**
 * Settlements, for the admin team. Morcast prepares each campaign's `settle` call once its review
 * has ended, and an admin signed in with the escrow's settler wallet sends it from the admin page.
 */

import { morcastEscrowAbi } from "@morcast/protocol";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Address, decodeFunctionData, type Hex } from "viem";
import { useConfig } from "wagmi";

import { api } from "../api/client.js";
import { callEscrow, EscrowCallError } from "../wallet/escrow.js";

/** A settlement as `GET /api/admin/settlements` describes it. */
export interface Settlement {
  id: number;
  chainId: number;
  escrow: string;
  campaignId: string;
  revision: number;
  resultHash: string;
  recognized: string;
  merkleRoot: string;
  /** The escrow's settler when the call was prepared, the wallet that sends it. */
  settler: string;
  /** The prepared call, to the escrow with this calldata and no value. */
  call: { to: string; value: string; data: string };
  status: "prepared" | "proposed" | "executed" | "failed" | "withdrawn";
  /** Set once proposed to a Safe's transaction service, for a settler that is a Safe. */
  safeTxHash: string | null;
  safeNonce: number | null;
  preparedAt: string;
  executedTx: string | null;
  executedAt: string | null;
  withdrawnBy: string | null;
  withdrawnAt: string | null;
  note: string | null;
  campaign: {
    title: string;
    /** The accepted request the deposit funds, whose admin page holds the terms and brief. */
    requestId: number | null;
    /** The brand that deposited the budget. */
    brand: { name: string; wallet: string };
    status: string;
    token: { address: string; symbol: string | null; decimals: number | null };
    /** Base units, as decimal strings. */
    budget: string;
    target: string;
    startAt: string;
    endAt: string;
    settlementClosesAt: string;
  };
  /** What the call pays in base units: the creators' pool, Morcast's fee, the brand's refund. */
  split: { pool: string; fee: string; refund: string };
  /** The fee's withdrawal from the escrow to Morcast's treasury, once it happened. */
  feeWithdrawal: { tx: string; at: string } | null;
}

const SETTLEMENTS_KEY = ["admin-settlements"] as const;

/** Open settlements, or every settlement. */
export function useSettlements(status: "open" | "all") {
  return useQuery({
    queryKey: [...SETTLEMENTS_KEY, status],
    queryFn: async () =>
      (await api.get<{ settlements: Settlement[] }>(`/api/admin/settlements?status=${status}`))
        .settlements,
  });
}

/**
 * Sends a prepared settlement from the connected wallet, which must be the escrow's settler. It
 * sends the exact call the worker prepared and checked against the escrow, after checking that the
 * call settles this settlement's campaign with its result.
 *
 * @returns The transaction hash. The settlement shows as executed once the worker reads the
 *          escrow's `CampaignSettled` event.
 */
export function useSendSettlement() {
  const config = useConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settlement: Settlement) => {
      const call = decodeFunctionData({ abi: morcastEscrowAbi, data: settlement.call.data as Hex });
      if (
        call.functionName !== "settle" ||
        settlement.call.to.toLowerCase() !== settlement.escrow.toLowerCase() ||
        call.args[0] !== BigInt(settlement.campaignId) ||
        call.args[3].toLowerCase() !== settlement.resultHash.toLowerCase()
      ) {
        throw new EscrowCallError("This call does not settle the campaign with its result.");
      }
      return callEscrow(
        config,
        { chainId: settlement.chainId, escrow: settlement.escrow as Address },
        { functionName: "settle", args: call.args },
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: SETTLEMENTS_KEY }),
  });
}

/**
 * Withdraws a settled campaign's fee from the connected wallet. The escrow sends it to its
 * treasury, Morcast's settler wallet, whoever asks, so any admin can collect fees at any time.
 *
 * @returns The transaction hash. The fee shows as withdrawn once the worker reads the escrow's
 *          `FeeWithdrawn` event.
 */
export function useWithdrawFee() {
  const config = useConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settlement: Settlement) =>
      callEscrow(
        config,
        { chainId: settlement.chainId, escrow: settlement.escrow as Address },
        { functionName: "withdrawFee", args: [BigInt(settlement.campaignId)] },
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: SETTLEMENTS_KEY }),
  });
}

/** Withdraws an open settlement, so that the result can be revised. */
export function useWithdrawSettlement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; note: string }) =>
      api.post(`/api/admin/settlements/${input.id}/withdraw`, { note: input.note }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: SETTLEMENTS_KEY }),
  });
}
